import { z } from 'zod';

/**
 * Het offline pakket precies zoals `GET /template-versions/{uuid}/package` het
 * levert (zie `smartkeur-api/docs/02-api-contract.md` §Catalog). Het document is
 * niet in `data` verpakt en staat plat: `nodes`, `items` en `edges` zijn losse
 * lijsten die naar elkaar verwijzen.
 *
 * De opname-engine rekent op een genest pakket (`TemplatePackage`); de vertaling
 * staat in `adapter.ts`. Valideer hier, reken daarna zonder controles.
 */

const numberish = z
    .union([z.number(), z.string()])
    .nullable()
    .transform((value) => {
        if (value === null || value === '') {
            return null;
        }

        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : null;
    });

const amount = z.union([z.string(), z.number()]).transform((value) => Number(value).toFixed(2));

export const API_QUESTION_TYPES = [
    'options',
    'options_single_multi',
    'options_multi',
    'yes_no',
    'number',
    'text',
    'text_items',
] as const;

/** De elf conditiewaarden uit `graph-semantics.md`; een andere waarde is een pakketfout. */
export const conditionResultSchema = z.union([
    z.literal(-5),
    z.literal(-4),
    z.literal(-3),
    z.literal(-2),
    z.literal(-1),
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(4),
    z.literal(6),
    z.literal(8),
]);

export const apiPackageQuestionOptionSchema = z.object({
    id: z.number().int(),
    legacy_id: z.number().int().nullable().default(null),
    label: z.string(),
    sort_order: z.number().int(),
    is_exclusive: z.boolean().default(false),
    is_clean: z.boolean().optional(),
});

export const apiPackageQuestionSchema = z.object({
    id: z.number().int(),
    legacy_id: z.number().int().nullable().default(null),
    type: z.enum(API_QUESTION_TYPES),
    label: z.string(),
    help_text: z.string().nullable().default(null),
    photo_requirement: z.enum(['none', 'optional', 'required']).default('none'),
    photo_min: z.number().int().nullable().default(null),
    number_min: numberish.default(null),
    number_max: numberish.default(null),
    number_unit: z.string().nullable().default(null),
    number_decimals: z.number().int().nullable().default(null),
    special: z.enum(['none', 'build_year']).default('none'),
    ask_once: z.boolean().default(false),
    sort_order: z.number().int(),
    options: z.array(apiPackageQuestionOptionSchema).default([]),
});

export const apiPackageNodeSchema = z.object({
    id: z.number().int(),
    parent_id: z.number().int().nullable(),
    legacy_id: z.number().int().nullable().default(null),
    name: z.string(),
    path: z.string().optional(),
    depth: z.number().int().optional(),
    sort_order: z.number().int(),
    section_path: z.string().nullable().optional(),
    report_section_path: z.string().nullable().optional(),
    report_level: z.number().int().nullable().optional(),
    nhg_code: z.string().nullable().default(null),
    is_multi: z.boolean().default(false),
    multi_question_label: z.string().nullable().default(null),
    multi_instance_label: z.string().nullable().default(null),
    multi_report_text: z.string().nullable().default(null),
    in_report: z.boolean().default(true),
});

export const apiPackageItemSchema = z.object({
    id: z.number().int(),
    node_id: z.number().int(),
    question_id: z.number().int(),
    legacy_id: z.number().int().nullable().default(null),
    is_start: z.boolean(),
    sort_order: z.number().int(),
});

export const apiPackageEdgeSchema = z.object({
    id: z.number().int(),
    item_id: z.number().int(),
    /**
     * `null` voor een rand die naar een uit de vraag verwijderde optie wijst
     * (749 van de 7304 randen in NTA 8060). Zie `adapter.ts`.
     */
    question_option_id: z.number().int().nullable(),
    question_option_legacy_id: z.number().int().nullable().optional(),
    sort_order: z.number().int(),
    result: conditionResultSchema.nullable(),
    next_item_id: z.number().int().nullable(),
    report_text: z.string().nullable().default(null),
    direct_cost: amount,
    term_cost: amount,
    improvement_cost: amount,
    cost_group_id: z.number().int().nullable().default(null),
    /** `none` · `answer_number`. De engine leidt de vermenigvuldiger af uit het vraagtype. */
    cost_multiplier_source: z.string().optional(),
    is_clean: z.boolean(),
    /**
     * `direct` · `term` · `improvement`: deze keuze heeft geen vast bedrag, de
     * inspecteur vult het zelf in. Pakketten van vóór dit veld hebben het niet.
     */
    amount_prompt: z.enum(['direct', 'term', 'improvement']).nullable().default(null),
});

export const apiPackageCostGroupSchema = z.object({
    id: z.number().int(),
    legacy_id: z.number().int().nullable().optional(),
    code: z.string().nullable().default(null),
    name: z.string(),
    long_name: z.string().nullable().default(null),
    sort_order: z.number().int(),
});

export const apiPackageVersionSchema = z.object({
    uuid: z.string(),
    version: z.number().int(),
    legacy_version: z.number().int().nullable().optional(),
    status: z.string(),
    published_at: z.string().nullable().optional(),
    question_count: z.number().int().optional(),
    node_count: z.number().int().optional(),
    template: z.object({
        uuid: z.string(),
        name: z.string(),
        inspection_type: z.object({
            uuid: z.string().optional(),
            code: z.string(),
            name: z.string(),
        }),
    }),
});

export const apiTemplatePackageSchema = z.object({
    version: apiPackageVersionSchema,
    cost_groups: z.array(apiPackageCostGroupSchema),
    questions: z.array(apiPackageQuestionSchema),
    nodes: z.array(apiPackageNodeSchema),
    items: z.array(apiPackageItemSchema),
    edges: z.array(apiPackageEdgeSchema),
    checksum: z.string(),
    generated_at: z.string(),
});

export type ApiTemplatePackage = z.infer<typeof apiTemplatePackageSchema>;
export type ApiPackageQuestion = z.infer<typeof apiPackageQuestionSchema>;
export type ApiPackageNode = z.infer<typeof apiPackageNodeSchema>;
export type ApiPackageItem = z.infer<typeof apiPackageItemSchema>;
export type ApiPackageEdge = z.infer<typeof apiPackageEdgeSchema>;
export type ApiPackageVersion = z.infer<typeof apiPackageVersionSchema>;

/* -------------------------------------------------------------------------- */
/* Waar de bedragen vandaan komen                                              */
/* -------------------------------------------------------------------------- */

const count = z.number().int();

/**
 * (`GET /template-cost-sources`, `Catalog\Queries\TemplateCostSources`) per
 * vragenlijst met een huidige versie, in getallen: welke antwoorden een bedrag
 * dragen, wat voor bedrag, welke NHG-onderdelen de lijst gebruikt, en wat de lijst
 * niet vastlegt. `price_level` en `vat_included` zijn `null` zolang de vragenlijst
 * daar niets over zegt.
 */
export const templateCostSourceResourceSchema = z.object({
    template_uuid: z.string(),
    template_name: z.string(),
    is_platform: z.boolean(),
    version_uuid: z.string(),
    version: count,
    legacy_version: count.nullable(),
    source_label: z.string(),
    imported_at: z.string().nullable(),
    published_at: z.string().nullable(),
    options_with_amounts: count,
    by_urgency: z.object({ direct: count, termijn: count, onderhoud: count }),
    unit_price_options: count,
    entered_amount_options: count,
    fixed_amount_options: count,
    lowest_amount: z.string().nullable(),
    highest_amount: z.string().nullable(),
    cost_groups: z.array(z.object({ name: z.string().nullable(), options: count })),
    nhg: z.object({
        nodes_with_code: count,
        codes_used: z.array(z.string()),
        codes_unused: z.array(z.string()),
        options_without_code: count,
    }),
    price_level: z.string().nullable(),
    vat_included: z.boolean().nullable(),
});

export type TemplateCostSourceResource = z.infer<typeof templateCostSourceResourceSchema>;
